import axios, { AxiosRequestConfig } from "axios"

import { forceUpdateToken, updateToken } from "@/plugins/keycloak"
import { router } from "@/router"
import { BASE_URL } from "@/services/endpoints"

const AUTHORIZATION_HEADER = "Authorization"
const AUTH_RETRY_MARKER = "_authRetry"

type AuthRetryConfig = AxiosRequestConfig & {
  [AUTH_RETRY_MARKER]?: boolean
}

export const applyInterceptors = () => {
  axios.interceptors.request.use(async config => {
    const token = await updateToken().catch(() => {
      throw new axios.Cancel("Authentication refresh failed")
    })
    if (config.headers) {
      config.headers[AUTHORIZATION_HEADER] = `Bearer ${token}`
      config.baseURL = BASE_URL
    }

    if (config.url && config.url.includes("//")) {
      const message = `Request canceled because the parameter was not found in the URL ${config.url}.`
      throw new axios.Cancel(message)
    }
    return config
  })

  axios.interceptors.response.use(
    response => response,
    async error => {
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        await router.push({ name: "ServiceUnavailable" })

        return Promise.reject(new Error("Service unavailable"))
      }

      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      if (!error.response) {
        return Promise.reject(error)
      }

      if (
        axios.isAxiosError(error) &&
        error.response.status === 401 &&
        error.config &&
        !(error.config as AuthRetryConfig)[AUTH_RETRY_MARKER]
      ) {
        const originalRequest = error.config as AuthRetryConfig
        originalRequest[AUTH_RETRY_MARKER] = true

        const token = await forceUpdateToken().catch(() => {
          throw new axios.Cancel("Authentication refresh failed")
        })
        if (token && originalRequest.headers) {
          originalRequest.headers[AUTHORIZATION_HEADER] = `Bearer ${token}`
        }

        return axios(originalRequest)
      }

      return Promise.reject(error)
    }
  )
}
